/**
 * MeetingIntegrationWizard.jsx
 * 
 * CANONICAL SPEC: Voice Input Agent (Meeting) Setup Wizard
 * 
 * This wizard is ONLY for voice_input_agent with meeting integration.
 * It must NEVER show Twilio/Telnyx or any telephony options.
 * 
 * Steps:
 * 1. Platform Connection - Connect Teams/Meet/Zoom
 * 2. Capture Rules - What to capture from meetings
 * 3. Output Destination - Where results go
 * 4. Review & Activate - Summary and activation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';

import {
  ArrowSquareOut,
  CaretLeft,
  CaretRight,
  ChatText,
  Check,
  CheckCircle,
  CircleNotch,
  Clock,
  Database,
  Envelope,
  FileText,
  GearSix,
  ListChecks,
  UsersThree,
  Video,
  Warning,
  WarningCircle,
  WebhooksLogo
} from '@phosphor-icons/react';
// Meeting platform options - NO TELEPHONY
const MEETING_PLATFORMS = [
  {
    id: 'microsoft_teams',
    name: 'Microsoft Teams',
    icon: '🟦',
    description: 'Connect to Microsoft Teams meetings',
    status: 'available'
  },
  {
    id: 'google_meet',
    name: 'Google Meet',
    icon: '🟢',
    description: 'Connect to Google Meet calls',
    status: 'available'
  },
  {
    id: 'zoom',
    name: 'Zoom',
    icon: '🔵',
    description: 'Connect to Zoom meetings',
    status: 'available'
  }
];

// Capture rule options
const CAPTURE_OPTIONS = [
  { id: 'transcript', label: 'Meeting Transcript', description: 'Full verbatim transcript', default: true },
  { id: 'summary', label: 'Summary', description: 'Executive summary of the meeting', default: true },
  { id: 'action_items', label: 'Action Items', description: 'Tasks and follow-ups with owners', default: true },
  { id: 'decisions', label: 'Decisions', description: 'Key decisions made during the meeting', default: true },
  { id: 'risks', label: 'Risks & Blockers', description: 'Identified risks and blockers', default: false },
  { id: 'followups', label: 'Follow-ups', description: 'Items requiring follow-up', default: false }
];

// Advanced capture options
const ADVANCED_CAPTURE_OPTIONS = [
  { id: 'speaker_attribution', label: 'Speaker Attribution', description: 'Identify who said what' },
  { id: 'timestamps', label: 'Time-stamped Notes', description: 'Include timestamps in notes' }
];

// Output destination options
const OUTPUT_DESTINATIONS = [
  { id: 'agent_result', label: 'Agent Run Result', description: 'View in agent run history', default: true, icon: Database },
  { id: 'knowledge_base', label: 'Knowledge Base', description: 'Save to organization knowledge base', default: false, icon: FileText },
  { id: 'email', label: 'Email (M365 / Gmail)', description: 'Send summary via email', default: false, icon: Envelope },
  { id: 'webhook', label: 'WebhooksLogo', description: 'Send to external endpoint', default: false, icon: WebhooksLogo }
];

const MeetingIntegrationWizard = () => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const { activeOrganization } = useOrganization();

  // PUBLIC BETA: Meeting integrations are disabled
  const BETA_DISABLED = true;

  if (BETA_DISABLED) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center p-4">
        <div className="max-w-md w-full glass rounded-2xl p-8 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-[#6366F1] to-[#EC4899] rounded-full flex items-center justify-center mx-auto mb-6">
            <Video className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Coming Soon</h1>
          <p className="text-[#A0AEC0] mb-6">
            Live meeting integrations (Teams, Zoom, Google Meet) are coming in our next release.
          </p>
          <p className="text-sm text-[#718096] mb-6">
            For now, you can upload recorded meetings as audio or video files to any text agent that accepts those inputs.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [agent, setAgent] = useState(null);

  // Step 1: Platform Connection
  const [connectedPlatforms, setConnectedPlatforms] = useState({});
  const [connecting, setConnecting] = useState(null);

  // Step 2: Capture Rules
  const [captureRules, setCaptureRules] = useState(
    CAPTURE_OPTIONS.reduce((acc, opt) => ({ ...acc, [opt.id]: opt.default }), {})
  );
  const [advancedCapture, setAdvancedCapture] = useState({
    speaker_attribution: true,
    timestamps: false
  });

  // Step 3: Output Destinations
  const [outputDestinations, setOutputDestinations] = useState(
    OUTPUT_DESTINATIONS.reduce((acc, opt) => ({ ...acc, [opt.id]: opt.default }), {})
  );
  const [webhookUrl, setWebhookUrl] = useState('');

  // Wizard steps
  const steps = [
    { id: 'platform', title: 'Connect Platform', icon: Video },
    { id: 'capture', title: 'Capture Rules', icon: ListChecks },
    { id: 'output', title: 'Output Destination', icon: FileText },
    { id: 'review', title: 'Review & Activate', icon: Check }
  ];

  // Load agent data
  useEffect(() => {
    const loadAgent = async () => {
      if (!agentId || !activeOrganization) return;

      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/agents/${agentId}`, {
          headers: { 'X-Organization-Id': activeOrganization.organization_id },
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setAgent(data);

          // Pre-fill from existing config
          const config = data.meeting_config || {};
          if (config.connected_platforms) setConnectedPlatforms(config.connected_platforms);
          if (config.capture_rules) setCaptureRules({ ...captureRules, ...config.capture_rules });
          if (config.advanced_capture) setAdvancedCapture({ ...advancedCapture, ...config.advanced_capture });
          if (config.output_destinations) setOutputDestinations({ ...outputDestinations, ...config.output_destinations });
          if (config.webhook_url) setWebhookUrl(config.webhook_url);
        }
      } catch (err) {
        console.error('Error loading agent:', err);
        setError('Failed to load agent');
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, [agentId, activeOrganization]);

  // Handle platform connection (OAuth flow placeholder)
  const handleConnectPlatform = async (platformId) => {
    setConnecting(platformId);
    try {
      // Simulate OAuth connection - in production this would redirect to OAuth
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setConnectedPlatforms(prev => ({
        ...prev,
        [platformId]: {
          connected: true,
          connected_at: new Date().toISOString(),
          account: 'user@example.com'
        }
      }));
    } catch (err) {
      setError('Failed to connect platform');
    } finally {
      setConnecting(null);
    }
  };

  // Handle platform disconnection
  const handleDisconnectPlatform = (platformId) => {
    setConnectedPlatforms(prev => {
      const updated = { ...prev };
      delete updated[platformId];
      return updated;
    });
  };

  // Toggle capture rule
  const toggleCaptureRule = (ruleId) => {
    setCaptureRules(prev => ({ ...prev, [ruleId]: !prev[ruleId] }));
  };

  // Toggle advanced capture
  const toggleAdvancedCapture = (optionId) => {
    setAdvancedCapture(prev => ({ ...prev, [optionId]: !prev[optionId] }));
  };

  // Toggle output destination
  const toggleOutputDestination = (destId) => {
    // Agent result is always enabled
    if (destId === 'agent_result') return;
    setOutputDestinations(prev => ({ ...prev, [destId]: !prev[destId] }));
  };

  // Save wizard configuration
  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const meetingConfig = {
        connected_platforms: connectedPlatforms,
        capture_rules: captureRules,
        advanced_capture: advancedCapture,
        output_destinations: outputDestinations,
        webhook_url: outputDestinations.webhook ? webhookUrl : null,
        configured_at: new Date().toISOString()
      };

      const response = await fetch(`${BACKEND_URL}/api/agents/${agentId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': activeOrganization?.organization_id
        },
        credentials: 'include',
        body: JSON.stringify({
          meeting_config: meetingConfig,
          status: 'active',
          wizard_completed: true,
          wizard_completed_at: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      navigate(`/partner-pro/ai-workforce/agents/${agentId}`);
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Step navigation
  const canProceed = useCallback(() => {
    switch (steps[currentStep]?.id) {
      case 'platform':
        return Object.keys(connectedPlatforms).length > 0;
      case 'capture':
        return Object.values(captureRules).some(v => v);
      case 'output':
        return outputDestinations.agent_result;
      case 'review':
        return true;
      default:
        return false;
    }
  }, [currentStep, connectedPlatforms, captureRules, outputDestinations, steps]);

  const handleNext = () => {
    if (currentStep < steps.length - 1 && canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Count connected platforms
  const connectedCount = Object.keys(connectedPlatforms).length;

  // Render step content
  const renderStepContent = () => {
    switch (steps[currentStep]?.id) {
      case 'platform':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Connect Meeting Platform</h3>
              <p className="text-[#A0AEC0] text-sm">
                Connect one or more meeting platforms. The agent will automatically join meetings from connected platforms.
              </p>
            </div>

            <div className="space-y-3">
              {MEETING_PLATFORMS.map(platform => {
                const isConnected = connectedPlatforms[platform.id]?.connected;
                
                return (
                  <div
                    key={platform.id}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isConnected
                        ? 'border-green-500/50 bg-green-500/10'
                        : 'border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.02)]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-3xl">{platform.icon}</span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-semibold">{platform.name}</span>
                            {isConnected && (
                              <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">
                                Connected
                              </span>
                            )}
                          </div>
                          <p className="text-[#A0AEC0] text-sm mt-1">
                            {isConnected 
                              ? connectedPlatforms[platform.id].account 
                              : platform.description}
                          </p>
                        </div>
                      </div>
                      
                      {isConnected ? (
                        <button
                          onClick={() => handleDisconnectPlatform(platform.id)}
                          className="px-4 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          Disconnect
                        </button>
                      ) : (
                        <button
                          onClick={() => handleConnectPlatform(platform.id)}
                          disabled={connecting === platform.id}
                          className="px-4 py-2 text-sm bg-[#6366F1] hover:bg-[#5558DD] text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          {connecting === platform.id ? (
                            <>
                              <CircleNotch size={14} className="animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            <>
                              <ArrowSquareOut size={14} />
                              Connect
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {connectedCount === 0 && (
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-2 text-amber-400">
                  <WarningCircle size={18} />
                  <span className="text-sm">Connect at least one platform to continue</span>
                </div>
              </div>
            )}
          </div>
        );

      case 'capture':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">What Should the Agent Capture?</h3>
              <p className="text-[#A0AEC0] text-sm">
                Select what information the agent should extract from your meetings.
              </p>
            </div>

            {/* Main capture options */}
            <div className="space-y-3">
              {CAPTURE_OPTIONS.map(option => (
                <button
                  key={option.id}
                  onClick={() => toggleCaptureRule(option.id)}
                  className={`w-full p-4 rounded-xl border text-left transition-all ${
                    captureRules[option.id]
                      ? 'border-[#6366F1] bg-[#6366F1]/10'
                      : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-white font-medium">{option.label}</span>
                      <p className="text-[#A0AEC0] text-sm mt-1">{option.description}</p>
                    </div>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                      captureRules[option.id]
                        ? 'bg-[#6366F1] border-[#6366F1]'
                        : 'border-[rgba(255,255,255,0.3)]'
                    }`}>
                      {captureRules[option.id] && <Check size={14} className="text-white" />}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Advanced options */}
            <div className="pt-4 border-t border-[rgba(255,255,255,0.1)]">
              <h4 className="text-sm font-medium text-white mb-3">Advanced Options</h4>
              <div className="space-y-3">
                {ADVANCED_CAPTURE_OPTIONS.map(option => (
                  <button
                    key={option.id}
                    onClick={() => toggleAdvancedCapture(option.id)}
                    className="w-full p-3 rounded-lg border border-[rgba(255,255,255,0.1)] flex items-center justify-between text-left hover:border-[rgba(255,255,255,0.2)] transition-colors"
                  >
                    <div>
                      <span className="text-white text-sm">{option.label}</span>
                      <p className="text-[#A0AEC0] text-xs mt-0.5">{option.description}</p>
                    </div>
                    <div className={`w-10 h-5 rounded-full transition-colors ${
                      advancedCapture[option.id] ? 'bg-[#6366F1]' : 'bg-[#374151]'
                    }`}>
                      <div className={`w-4 h-4 rounded-full bg-white transform transition-transform mt-0.5 ${
                        advancedCapture[option.id] ? 'translate-x-5' : 'translate-x-0.5'
                      }`} />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'output':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Where Should Results Go?</h3>
              <p className="text-[#A0AEC0] text-sm">
                Choose where meeting notes and outputs should be saved or sent.
              </p>
            </div>

            <div className="space-y-3">
              {OUTPUT_DESTINATIONS.map(dest => {
                const Icon = dest.icon;
                const isDefault = dest.id === 'agent_result';
                
                return (
                  <div key={dest.id}>
                    <button
                      onClick={() => toggleOutputDestination(dest.id)}
                      disabled={isDefault}
                      className={`w-full p-4 rounded-xl border text-left transition-all ${
                        outputDestinations[dest.id]
                          ? 'border-[#6366F1] bg-[#6366F1]/10'
                          : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                      } ${isDefault ? 'cursor-default' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            outputDestinations[dest.id] ? 'bg-[#6366F1]/20' : 'bg-[rgba(255,255,255,0.05)]'
                          }`}>
                            <Icon size={18} className={outputDestinations[dest.id] ? 'text-[#6366F1]' : 'text-[#A0AEC0]'} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{dest.label}</span>
                              {isDefault && (
                                <span className="px-2 py-0.5 text-xs bg-[rgba(255,255,255,0.1)] text-[#A0AEC0] rounded">
                                  Always enabled
                                </span>
                              )}
                            </div>
                            <p className="text-[#A0AEC0] text-sm mt-1">{dest.description}</p>
                          </div>
                        </div>
                        {!isDefault && (
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            outputDestinations[dest.id]
                              ? 'bg-[#6366F1] border-[#6366F1]'
                              : 'border-[rgba(255,255,255,0.3)]'
                          }`}>
                            {outputDestinations[dest.id] && <Check size={14} className="text-white" />}
                          </div>
                        )}
                      </div>
                    </button>

                    {/* WebhooksLogo URL input */}
                    {dest.id === 'webhook' && outputDestinations.webhook && (
                      <div className="mt-3 ml-12">
                        <input
                          type="url"
                          value={webhookUrl}
                          onChange={(e) => setWebhookUrl(e.target.value)}
                          placeholder="https://your-webhook-endpoint.com/..."
                          className="w-full px-4 py-2 rounded-lg bg-[#1A1B23] border border-[rgba(255,255,255,0.1)] text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Review & Activate</h3>
              <p className="text-[#A0AEC0] text-sm">
                Review your configuration and activate the agent.
              </p>
            </div>

            {/* Summary Cards */}
            <div className="space-y-4">
              {/* Connected Platforms */}
              <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)]">
                <div className="flex items-center gap-3 mb-3">
                  <Video size={18} className="text-[#6366F1]" />
                  <span className="text-white font-medium">Connected Platforms</span>
                </div>
                <div className="flex flex-wrap gap-2 ml-7">
                  {Object.keys(connectedPlatforms).map(platformId => {
                    const platform = MEETING_PLATFORMS.find(p => p.id === platformId);
                    return (
                      <span key={platformId} className="px-3 py-1 bg-green-500/10 text-green-400 text-sm rounded-full flex items-center gap-1">
                        <span>{platform?.icon}</span>
                        {platform?.name}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Capture Rules */}
              <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)]">
                <div className="flex items-center gap-3 mb-3">
                  <ListChecks size={18} className="text-[#6366F1]" />
                  <span className="text-white font-medium">Capture Rules</span>
                </div>
                <ul className="ml-7 space-y-1">
                  {CAPTURE_OPTIONS.filter(opt => captureRules[opt.id]).map(opt => (
                    <li key={opt.id} className="text-[#A0AEC0] text-sm flex items-center gap-2">
                      <Check size={14} className="text-green-400" />
                      {opt.label}
                    </li>
                  ))}
                  {advancedCapture.speaker_attribution && (
                    <li className="text-[#A0AEC0] text-sm flex items-center gap-2">
                      <Check size={14} className="text-green-400" />
                      Speaker Attribution
                    </li>
                  )}
                  {advancedCapture.timestamps && (
                    <li className="text-[#A0AEC0] text-sm flex items-center gap-2">
                      <Check size={14} className="text-green-400" />
                      Time-stamped Notes
                    </li>
                  )}
                </ul>
              </div>

              {/* Output Destinations */}
              <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.1)]">
                <div className="flex items-center gap-3 mb-3">
                  <FileText size={18} className="text-[#6366F1]" />
                  <span className="text-white font-medium">Output Destinations</span>
                </div>
                <ul className="ml-7 space-y-1">
                  {OUTPUT_DESTINATIONS.filter(dest => outputDestinations[dest.id]).map(dest => (
                    <li key={dest.id} className="text-[#A0AEC0] text-sm flex items-center gap-2">
                      <Check size={14} className="text-green-400" />
                      {dest.label}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Activation Notice */}
            <div className="p-4 rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/30">
              <div className="flex items-start gap-3">
                <CheckCircle size={20} className="text-[#6366F1] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-semibold mb-1">Ready to Activate</p>
                  <p className="text-[#A0AEC0] text-sm">
                    Once activated, this agent will automatically join meetings from your connected platforms and generate notes when meetings end.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#161B26] flex items-center justify-center">
        <CircleNotch size={48} className="animate-spin text-[#6366F1]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161B26] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
            <span className="text-3xl">🎧</span>
            Meeting Integration Setup
          </h1>
          <p className="text-[#A0AEC0]">
            Configure how the agent joins and captures your meetings.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      index < currentStep
                        ? 'bg-[#10B981] text-white'
                        : index === currentStep
                        ? 'bg-[#6366F1] text-white'
                        : 'bg-[rgba(255,255,255,0.1)] text-[#A0AEC0]'
                    }`}
                  >
                    {index < currentStep ? (
                      <Check size={18} />
                    ) : (
                      <step.icon size={18} />
                    )}
                  </div>
                  <span className={`text-xs mt-2 ${
                    index === currentStep ? 'text-white' : 'text-[#A0AEC0]'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    index < currentStep ? 'bg-[#10B981]' : 'bg-[rgba(255,255,255,0.1)]'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Error WarningCircle */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-[#EF4444]/10 border border-[#EF4444]/30">
            <div className="flex items-center gap-3">
              <WarningCircle size={20} className="text-[#EF4444]" />
              <span className="text-[#EF4444]">{error}</span>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="glass rounded-2xl p-6 mb-6">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="px-6 py-3 rounded-full bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.15)] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <CaretLeft size={18} />
            Back
          </button>

          {currentStep === steps.length - 1 ? (
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-8 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <CircleNotch size={18} className="animate-spin" />
                  Activating...
                </>
              ) : (
                <>
                  Activate Agent
                  <Check size={18} />
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className="px-6 py-3 rounded-full bg-[#6366F1] hover:bg-[#5558DD] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              Continue
              <CaretRight size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingIntegrationWizard;
